import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { User, Clock, Trash2 } from 'lucide-react';
import { UPLOAD_PREFIX } from '../../config/appConfig';
import MessageService from '../../services/message.service';

const ConversationsList = ({
  conversations,
  onSelectConversation,
  onConversationDeleted,
  userRole,
  nameField = 'first_name',
  specialtyField = 'specialization'
}) => {
  const [imageErrors, setImageErrors] = useState({});

  const handleDeleteClick = async (e, convId) => {
    e.stopPropagation();
    
    if (window.confirm('Sei sicuro di voler eliminare questa conversazione? Questa azione non può essere annullata.')) {
      try {
        await MessageService.deleteUserMessages(convId);
        onConversationDeleted(convId);
      } catch (error) {
        console.error('Errore durante l\'eliminazione della conversazione:', error);
        alert('Si è verificato un errore durante l\'eliminazione della conversazione.');
      }
    }
  };

  const handleImageError = (convId) => {
    setImageErrors(prev => ({
      ...prev,
      [convId]: true
    }));
  };

  return (
    <div className="flex-1 overflow-y-auto bg-white">
      {conversations.map((conv) => (
        <div
          key={conv.id}
          className="relative flex items-center p-4 border-b hover:bg-gray-50 cursor-pointer group"
        >
          {/* Contenitore principale cliccabile */}
          <div 
            className="flex-1 flex items-center"
            onClick={() => onSelectConversation(conv.id)}
          >
            <div className="w-12 h-12 rounded-full flex items-center justify-center border-2 border-gray-200 overflow-hidden">
              {conv.therapist?.profile_picture && !imageErrors[conv.id] ? (
                <img
                  src={`${UPLOAD_PREFIX}/storage/${conv.therapist.profile_picture}`}
                  alt={conv[nameField]}
                  className="w-full h-full object-cover"
                  onError={() => handleImageError(conv.id)}
                />
              ) : (
                <div className="w-full h-full bg-purple-100 flex items-center justify-center">
                  <User className="w-6 h-6 text-purple-600" />
                </div>
              )}
            </div>

            <div className="flex-1 ml-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold">{`${conv.first_name} ${conv.last_name}`}</h3>
                  <p className="text-sm text-gray-500">{conv.therapist?.specialization}</p>
                </div>
                <div className="flex items-center">
                  <Clock className="w-4 h-4 text-gray-400 mr-1" />
                  <span className="text-sm text-gray-500">
                    {conv.messages?.[0]?.created_at}
                  </span>
                </div>
              </div>
              <p className="text-sm mt-1 text-gray-600">
                {conv.messages?.[0]?.content}
              </p>
            </div>
          </div>

          {/* Pulsante elimina */}
          <button
            onClick={(e) => handleDeleteClick(e, conv.id)}
            className="ml-4 p-2 rounded hover:bg-gray-100"
            title="Elimina conversazione"
          >
            <Trash2 className="w-5 h-5 text-gray-700" />
          </button>
        </div>
      ))}
    </div>
  );
};

ConversationsList.propTypes = {
  conversations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      therapist: PropTypes.shape({
        profile_picture: PropTypes.string,
        specialization: PropTypes.string
      }),
      messages: PropTypes.arrayOf(
        PropTypes.shape({
          content: PropTypes.string,
          created_at: PropTypes.string
        })
      )
    })
  ).isRequired,
  onSelectConversation: PropTypes.func.isRequired,
  onConversationDeleted: PropTypes.func.isRequired,
  userRole: PropTypes.oneOf(['patient', 'doctor']).isRequired,
  nameField: PropTypes.string,
  specialtyField: PropTypes.string
};

export default ConversationsList;